import { InsightRendererProps, InsightTag } from '@ecosystem/newron-client-collections/build/shared/types';
import { FC } from 'react';
import { PageName } from '@utils/pageConfig/Types';
import { CollectionInsightType } from './Model';
import DoubleBarChartInsight from './category-leaderboard-double-bar-chart/DoubleBarChartInsight';
import { getDoubleBarChartInsightInfoTags } from './category-leaderboard-double-bar-chart/DoubleBarChartInsightUtils';
import ProductsDoubleBarChartInsight from './products-leaderboard-double-bar-chart/DoubleBarChartInsight';
import { getProductsDoubleBarChartInsightInfoTags } from './products-leaderboard-double-bar-chart/DoubleBarChartInsightUtils';
import BubbleChartInsight from './opportunity-review-bubble-chart/BubbleChartInsight';
import { getBubbleChartInsightInfoTags } from './opportunity-review-bubble-chart/BubbleChartInsightUtils';
import { getStackedBarChartInsightInfoTags } from './performance-analysis-stacked-bar-chart/StackedBarChartInsightUtils';
import StackedBarChartRenderer from './performance-analysis-stacked-bar-chart/StackedBarChartRenderer';
import { getPerformanceAnalysisLineChartInsightInfoTags } from './performance-analysis-line-chart/LineChartInsightUtils';
import { PerformanceAnalysisLineChartInsightRenderer } from './performance-analysis-line-chart/LineChartInsight';
import { RetailerOverviewLineChartInsightRenderer } from './retailer-overview-line-chart/LineChartInsight';
import { getRetailerOverviewLineChartInsightInfoTags } from './retailer-overview-line-chart/LineChartInsightUtils';
import { ChartType } from '@ecosystem/newron-client-lib';
import CombinedChartInsightRenderer from './retailer-overview-combined-chart/CombinedChartInsightRenderer';
import { getCombinedChartInsightInfoTags } from './retailer-overview-combined-chart/CombinedChartInsightUtils';
import { TrendStackBarChartInsightRenderer } from './explore-trends-stacked-bar-chart/TrendStackBarChartInsight';
import { getTrendStackBarChartInsightInfoTags } from './explore-trends-stacked-bar-chart/TrendStackBarChartInsightUtils';
import ButterflyChartInsight from '@components/collections/trading-assistant-butterfly-chart/ButterflyChartInsight';
import { getButterflyChartInsightInfoTags } from '@components/collections/trading-assistant-butterfly-chart/ButterflyChartInsightUtils';
import { ProductsMarketRetailerLineChartInsightRenderer } from './products-trends-market-and-retailer-line-chart/LineChartInsight';

export interface CollectionsRegister {
  collectionsRenderer: FC<InsightRendererProps<any>>;
  insightName: string;
  insightType: string;
  chartType: ChartType;
  pageName: PageName;
  getChartInsightInfoTags: (insight: any) => Promise<InsightTag[]>;
}

export const collectionsRegister: CollectionsRegister[] = [
  {
    collectionsRenderer: PerformanceAnalysisLineChartInsightRenderer as FC<InsightRendererProps<any>>,
    insightName: 'Performance Analysis Line Chart',
    insightType: CollectionInsightType.PerformanceAnalysisLineChart,
    chartType: 'Line Chart',
    pageName: PageName.PERFORMANCE_ANALYSIS,
    getChartInsightInfoTags: getPerformanceAnalysisLineChartInsightInfoTags as (insight: any) => Promise<InsightTag[]>,
  },
  {
    collectionsRenderer: DoubleBarChartInsight as FC<InsightRendererProps<any>>,
    insightName: 'Category Leaderboard Bar Chart',
    insightType: CollectionInsightType.CategoryLeaderboardBarChart,
    chartType: 'Bar Chart',
    pageName: PageName.CATEGORY_LEADERBOARD,
    getChartInsightInfoTags: getDoubleBarChartInsightInfoTags as (insight: any) => Promise<InsightTag[]>,
  },
  {
    collectionsRenderer: StackedBarChartRenderer as FC<InsightRendererProps<any>>,
    insightName: 'Performance Analysis Stacked Bar Chart',
    insightType: CollectionInsightType.PerformanceAnalysisStackedBarChart,
    chartType: 'Stacked Bar Chart',
    pageName: PageName.PERFORMANCE_ANALYSIS,
    getChartInsightInfoTags: getStackedBarChartInsightInfoTags as (insight: any) => Promise<InsightTag[]>,
  },
  {
    collectionsRenderer: BubbleChartInsight as FC<InsightRendererProps<any>>,
    insightName: 'Opportunity Review Bubble Chart',
    insightType: CollectionInsightType.OpportunityReviewBubbleChart,
    chartType: 'Bubble Chart',
    pageName: PageName.OPPORTUNITY_REVIEW,
    getChartInsightInfoTags: getBubbleChartInsightInfoTags as (insight: any) => Promise<InsightTag[]>,
  },
  {
    collectionsRenderer: CombinedChartInsightRenderer as FC<InsightRendererProps<any>>,
    insightName: 'Retailer Overview Performance Trend ',
    insightType: CollectionInsightType.RetailerOverviewCombinedChart,
    chartType: 'Combined Chart',
    pageName: PageName.RETAILER_OVERVIEW,
    getChartInsightInfoTags: getCombinedChartInsightInfoTags as (insight: any) => Promise<InsightTag[]>,
  },
  {
    collectionsRenderer: RetailerOverviewLineChartInsightRenderer as FC<InsightRendererProps<any>>,
    insightName: 'Retailer Overview Average Selling Price',
    insightType: CollectionInsightType.RetailerOverviewLineChart,
    chartType: 'Line Chart',
    pageName: PageName.RETAILER_OVERVIEW,
    getChartInsightInfoTags: getRetailerOverviewLineChartInsightInfoTags as (insight: any) => Promise<InsightTag[]>,
  },
  {
    collectionsRenderer: DoubleBarChartInsight as FC<InsightRendererProps<any>>,
    insightName: 'Explore Bar Chart',
    insightType: CollectionInsightType.ExploreBarChart,
    chartType: 'Bar Chart',
    pageName: PageName.EXPLORE,
    getChartInsightInfoTags: getDoubleBarChartInsightInfoTags as (insight: any) => Promise<InsightTag[]>,
  },
  {
    collectionsRenderer: PerformanceAnalysisLineChartInsightRenderer as FC<InsightRendererProps<any>>,
    insightName: 'Explore Line Chart',
    insightType: CollectionInsightType.ExploreLineChart,
    chartType: 'Line Chart',
    pageName: PageName.EXPLORE,
    getChartInsightInfoTags: getPerformanceAnalysisLineChartInsightInfoTags as (insight: any) => Promise<InsightTag[]>,
  },
  {
    collectionsRenderer: ProductsMarketRetailerLineChartInsightRenderer,
    insightName: 'Products Line Chart',
    insightType: CollectionInsightType.ProductTrendsMarketRetailerLineChart,
    chartType: 'Line Chart',
    pageName: PageName.RI_PRODUCT,
    getChartInsightInfoTags: getPerformanceAnalysisLineChartInsightInfoTags,
  },
  {
    collectionsRenderer: StackedBarChartRenderer as FC<InsightRendererProps<any>>,
    insightName: 'Explore Stacked Chart',
    insightType: CollectionInsightType.ExploreSegmentsStackedBarChart,
    chartType: 'Stacked Bar Chart',
    pageName: PageName.EXPLORE,
    getChartInsightInfoTags: getStackedBarChartInsightInfoTags as (insight: any) => Promise<InsightTag[]>,
  },
  {
    collectionsRenderer: TrendStackBarChartInsightRenderer as FC<InsightRendererProps<any>>,
    insightName: 'Trended Stacked Bar Chart',
    insightType: CollectionInsightType.TrendStackBarChart,
    chartType: 'Stacked Bar Chart',
    pageName: PageName.EXPLORE,
    getChartInsightInfoTags: getTrendStackBarChartInsightInfoTags as (insight: any) => Promise<InsightTag[]>,
  },
  {
    collectionsRenderer: ButterflyChartInsight as FC<InsightRendererProps<any>>,
    insightName: 'Trading Assistant Butterfly Chart',
    insightType: CollectionInsightType.TradingAssistantButterflyChart,
    chartType: 'Butterfly Chart',
    pageName: PageName.TRADING_ASSISTANT,
    getChartInsightInfoTags: getButterflyChartInsightInfoTags as (insight: any) => Promise<InsightTag[]>,
  },
  {
    collectionsRenderer: ProductsDoubleBarChartInsight,
    insightName: 'Products Leaderboard Bar Chart',
    insightType: CollectionInsightType.ProductsDoubleBarChart,
    chartType: 'Bar Chart',
    pageName: PageName.RI_PRODUCT,
    getChartInsightInfoTags: getProductsDoubleBarChartInsightInfoTags,
  },
];
